import { getCenter } from "ol/extent";
import Point from "ol/geom/Point";
import { Circle, Fill, Icon, Stroke, Style } from "ol/style";

import markerImg from "../img/marker.png";

import debugVehicleStyleFunc from "./debugVehicleStyleFunc";

import type {
  RealtimeLayer,
  RealtimeStopSequence,
  RealtimeTrajectory,
  RealtimeTrajectoryProperties,
} from "mobility-toolbox-js/types";
import type { Feature, Map, View } from "ol";
import type { Extent } from "ol/extent";
import type { StyleFunction } from "ol/style/Style";
import type { SVGProps } from "react";

import type TralisLayer from "../TralisLayer";

const {
  VITE_API_KEY,
  VITE_CONFIG,
  VITE_MAPS_URL,
  VITE_NOTIFICATION_KEY,
  VITE_NOTIFICATION_URL,
  VITE_REALTIME_URL,
  VITE_SENTRY_ENVIRONMENT,
  VITE_SENTRY_RELEASE,
  VITE_STYLE_TOPOGRAPHIC,
}: Record<string, string> = import.meta.env;

const { searchParams } = new URL(window.location.href);

const isProd = VITE_SENTRY_ENVIRONMENT === "prod";

const realtimeApiUrl =
  (!isProd && searchParams.get("realtimeurl")) || VITE_REALTIME_URL;

const mapsApiUrl = (!isProd && searchParams.get("mapsurl")) || VITE_MAPS_URL;

const notificationApiUrl =
  (!isProd && searchParams.get("notificationurl")) || VITE_NOTIFICATION_URL;

export const getStyleUrl = (style: string) =>
  `${mapsApiUrl}/styles/${style}/style.json?key=${VITE_API_KEY}`;

export const markerStyle = new Style({
  image: new Icon({
    anchor: [0.5, 40],
    anchorXUnits: "fraction",
    anchorYUnits: "pixels",
    src: markerImg,
  }),
});

const fill = new Fill({
  color: "rgba(255, 255, 255, 0.01)",
});

export const stationStyle = new Style({
  fill,
  image: new Circle({
    fill,
    radius: 20,
  }),
  stroke: new Stroke({
    color: "rgba(255, 255, 255, 0.01)",
    width: 25,
  }),
});

const styleTopographic = VITE_STYLE_TOPOGRAPHIC || "travic_v2_sbahn";

export interface MapLineIcon {
  arrow: string;
  circle: string;
}

export type MapLineIcons = Record<string, MapLineIcon>;

export type LineIcons = Record<string, React.ReactNode>;

export interface MapLineIconProps {
  arrowSvgProps: SVGProps<SVGSVGElement>;
  circleProps: {
    "fill-opacity"?: string;
    "stroke-width"?: string;
  } & SVGProps<SVGCircleElement>;
  // For circle svg icon.
  svgProps: SVGProps<SVGSVGElement>;
  textProps: {
    "dominant-baseline"?: string;
    "font-family"?: string;
    "font-size"?: string;
    "font-weight"?: string;
    "text-anchor"?: string;
  } & SVGProps<SVGTextElement>;
}

export interface LineIconsProps {
  innerRect?: SVGProps<SVGRectElement>;
  rect: SVGProps<SVGRectElement>;
  rx: SVGProps<SVGCircleElement>["rx"];
  text: SVGProps<SVGTextElement>;
  viewBox: SVGProps<SVGSVGElement>["viewBox"];
}

export type VehicleStyleFunction = (
  trajectory: {
    isStanding: boolean;
    vehicle_number: string;
  } & RealtimeTrajectoryProperties,
  layer: TralisLayer,
  vehicleCanvas: HTMLCanvasElement,
  mainCanvas?: HTMLCanvasElement,
) => HTMLCanvasElement | null | undefined;

export type GetCustomMapIconForTrajectory = (
  trajectory: RealtimeTrajectoryProperties,
  lineImgKey: string,
  lineImages: MapLineIcons,
) => {
  arrow?: string;
  circle?: string;
  deltaScale?: number;
  key?: string;
} | null;

export interface EventConfigProperties {
  from: string;
  onClick?: () => void;
  stationBanner?: React.ReactNode;
  stationUic?: number;
  style: (map: Map) => () => null | Style | undefined;
  until: string;
}
export type EventConfig = Feature<Point, EventConfigProperties>;

export interface ModeConfig {
  dataExtent?: Extent;
  events?: EventConfig[];
  extent?: Extent;
  getZoomFromPreviousMode?: (zoom: number) => number;
  highlightFullTrajectory?: boolean;
  maxZoom?: number;
  maxZoomForAutoMode?: number;
  minZoom?: number;
  minZoomForAutoMode?: number;
  paintLineWidthHighlight?: { stops: [number, number][] } | number;
  style?: string;
  styleUrl?: string;
  useHideOthersBg?: boolean;
  usePeripherieIcons?: boolean;
  x: number;
  y: number;
  z: number;
}

export interface Config {
  allNotificationsOpen?: boolean;

  apiKey: string;
  autoParam: boolean;
  bookmarks: boolean;
  bottomContainer: boolean;
  debugVehicleStyleFunc: VehicleStyleFunction;
  defaultLineIcon?: React.ReactNode;
  departuresList: boolean;
  departuresListCloseButton: boolean;
  dfltIconHighlightScale: number;
  dfltIconScale: number;
  disruptionInfo: boolean;
  disruptionInfoCloseButton: boolean;
  findTrainAuto: (vehicle: RealtimeTrajectory) => RealtimeTrajectory;
  fullExtent: boolean;
  getCustomMapIconForTrajectory?: GetCustomMapIconForTrajectory;
  getIconScaleFromRes?: (view: View, layer: RealtimeLayer) => number;
  initialStation: null | number | string;
  legalLinks: boolean;
  lineIcons?: LineIcons;
  lineIconsProps: LineIconsProps;
  lineParam: boolean;
  localStorageKey: string;
  mapControls: boolean;
  mapLineIconProps: MapLineIconProps;
  mapLineIcons?: MapLineIcons;
  mapUnderBottomContainer: boolean;
  maskInfo: boolean;
  motsRelation: boolean;
  navigatorClass: null | string;
  navigatorParam: boolean;
  notificationApiKey: string;
  notificationApiUrl: string;
  notificationControls: boolean;
  notificationTenant?: string;
  platformInfo: boolean;
  renderAffectedRouteTitle: React.FC<{
    properties: {
      stops: { name: string }[];
    };
  }>;
  renderDeparturesHeader: React.FC;
  renderRouteHeader: React.FC<{
    stopSequence?: RealtimeStopSequence;
    vehicle?: RealtimeTrajectoryProperties;
  }>;
  renderStickyRouteHeader: React.FC<{
    stopSequence?: RealtimeStopSequence;
    vehicle?: RealtimeTrajectoryProperties;
  }>;
  risPopup: boolean;
  routeInfo: boolean;
  routeInfoCloseButton: boolean;
  ["schematic"]: ModeConfig;
  selectedStationStyleFunc: StyleFunction;
  selectedVehicleStyleFunc: null | VehicleStyleFunction;
  sentryEnvironment: string;
  sentryRelease: string;
  sortVehicles?: (
    trajectory: RealtimeTrajectory,
    trajectory2: RealtimeTrajectory,
  ) => number;
  ssoConfig: string;
  standingVehicleStyleFunc: null | VehicleStyleFunction;
  stationIcons: {
    forceDisplayAccessibility: boolean;
    forceDisplayElevator: boolean;
    hasAccessibility: boolean;
    hasAirport: boolean;
    hasElevator: boolean;
    hasElevatorOutOfOrder: boolean;
    hasElevatorState: boolean;
    hasFerry: boolean;
    hasLongDistanceTrain: boolean;
    hasRegionalTrain: boolean;
    hasUBahn: boolean;
    hasZOB: boolean;
  };
  stationIconsLegend: boolean;
  stationIconsLegendPopup: boolean;
  stationParam: boolean;
  stationStyleFunc: StyleFunction;
  switchMode: boolean;
  tenant: string;
  title?: string;
  ["topographic"]: ModeConfig;
  trainParam: boolean;
  url: string;
  useNotifications: boolean;
  userInteractions: boolean;
  vehiclesList: boolean;
  xyzParam: boolean;
}

const common: Config = {
  apiKey: VITE_API_KEY,
  /**
   * Permalink functionnalities
   */
  autoParam: false,
  bookmarks: true,
  bottomContainer: true,
  // Return a canvas to show more informatsions about a vehicle in debug mode
  debugVehicleStyleFunc,
  departuresList: true,
  departuresListCloseButton: true,
  // Scale of icons when vehicle is highlighted
  dfltIconHighlightScale: 0.8,
  // Scale of icons by default.
  dfltIconScale: 0.6,

  disruptionInfo: true,

  disruptionInfoCloseButton: true,

  // When train=auto, function to determine whcich train to follow.
  findTrainAuto: (vehicle) => {
    return vehicle;
  },

  fullExtent: true,
  /**
   * Other functionnalities
   */
  // Define the default station selected when tha app is called with /navigator in the url.
  initialStation: null,
  legalLinks: true,
  // Line icons properties.
  // Default properties use to build SVGs using a react component .
  // These icons are used in RouteInfo, Departures and MotsRelations component.
  lineIconsProps: {
    rect: {
      height: "calc(100% - 2px)",
      width: "calc(100% - 2px)",
      x: 1,
      y: 1,
    },
    rx: 9,
    text: {
      dominantBaseline: "middle",
      fontFamily: "Arial",
      fontSize: "14px",
      fontWeight: "bold",
      textAnchor: "middle",
      x: "50%",
      y: "55%",
    },
    viewBox: "0 0 42 20",
  },
  lineParam: false,
  localStorageKey: "v0.3",
  /**
   * Components
   *
   * All components can be removed from the interface.
   * Here is the exhaustive list of them.
   */
  mapControls: true,
  // Map line icons properties
  // Default properties use to build SVGs as a string that represent the line on the map.
  mapLineIconProps: {
    // For arrow svg icon.
    arrowSvgProps: {
      height: "150",
      viewBox: "0 0 12.75 12.75",
      width: "150",
    },
    circleProps: {
      cx: "50%",
      cy: "50%",
      "fill-opacity": "0.85",
      r: "25%",
      "stroke-width": "5",
    },
    // For circle svg icon.
    svgProps: {
      height: "150",
      viewBox: "0 0 150 150",
      width: "150",
    },
    textProps: {
      "dominant-baseline": "middle",
      "font-family": "Arial",
      "font-size": "40px",
      "font-weight": "bold",
      "text-anchor": "middle",
      x: "50%",
      y: "52%",
    },
  },
  mapUnderBottomContainer: false, // use the full window width size for the map when the bottom container is displayed (only xl screen)
  maskInfo: true,
  motsRelation: false,
  // Add a css class to the navigator
  navigatorClass: null,
  navigatorParam: true,
  notificationApiKey: VITE_NOTIFICATION_KEY,
  notificationApiUrl,
  notificationControls: true,
  platformInfo: true,
  // Render functions properties
  renderAffectedRouteTitle: (affectedRoute) => {
    let start = "";
    let end = "";
    if (affectedRoute?.properties?.stops?.length) {
      const { stops } = affectedRoute.properties;
      const startStop = stops[0];
      const endStop = stops[stops.length - 1];
      start = startStop.name;
      end = endStop.name;
    }
    return `${start} ↔ ${end}`;
  },
  // Render a component between the RouteInfo header and the NotificationsList.
  // Before the sticky part.
  renderDeparturesHeader: () => {
    return null;
  },
  // Render a component between the RouteInfo header and the NotificationsList.
  // Before the sticky part.
  renderRouteHeader: () => {
    return null;
  },
  // Render a component in the sticky part of the RouteInfo header.
  renderStickyRouteHeader: () => {
    return null;
  },
  risPopup: true,

  routeInfo: true,
  routeInfoCloseButton: true,
  // Schematic mode dependent values. Same properties as in topogrpahic dependent values.
  schematic: {
    highlightFullTrajectory: true,
    useHideOthersBg: true,
    usePeripherieIcons: true,
    x: 0,
    y: 0,
    z: 0,
  },
  // OpenLayers style function when a station is selected.
  selectedStationStyleFunc: (feature) => {
    const geometry = (feature as Feature).getGeometry();
    if (geometry) {
      const c = getCenter(geometry.getExtent());
      markerStyle.setGeometry(new Point(c));
      return markerStyle;
    }
  },
  // Return a canvas to show when a vehicle is selected
  selectedVehicleStyleFunc: null,
  sentryEnvironment: `${VITE_SENTRY_ENVIRONMENT}`,

  /**
   * TralisLayer options
   */
  // will be set as filter options.
  // filterVehicles:(trajectory)=>{return true};

  // will be set as sort options.
  // sortVehicles:(trajectory)=>{return 1};

  sentryRelease: `${VITE_SENTRY_RELEASE}`,

  ssoConfig: "s-bahn-munchen",

  // Return a canvas to show when a vehicle is standing
  standingVehicleStyleFunc: null,

  stationIcons: {
    forceDisplayAccessibility: false, // Temporary hack for accessibility, see SBAHNHH-101.
    forceDisplayElevator: false, // Always display an icon even if hasElevator = false
    hasAccessibility: true,
    hasAirport: true,
    // Define which icon is supported
    hasElevator: true,
    hasElevatorOutOfOrder: true,
    hasElevatorState: false,
    hasFerry: false,
    hasLongDistanceTrain: true,
    hasRegionalTrain: true,
    hasUBahn: true,
    hasZOB: true,
  },

  /**
   * Style function
   */
  // Line icons used by LineIconSVG component. Must be define in each config.
  // lineIcons,

  // Default icon to use when the line icon is not found. Must be define in each config.
  // defaultLineIcon: lineIcons.s,

  // Line icons used by vehicle on the map. Must be define in each config.
  // mapLineIcons,

  stationIconsLegend: false,

  stationIconsLegendPopup: false,

  stationParam: true,

  // OpenLayers style function for the station, stations are styled by the mapbox style.
  // This style is used to allow click on them.
  stationStyleFunc: () => {
    return stationStyle;
  },

  switchMode: true,

  tenant: VITE_CONFIG,

  // Topographic mode dependent values
  topographic: {
    // Highlight or not the trajectory of the selected vehicle.
    highlightFullTrajectory: true,
    // Line width used to highlight a selected line. See `line` parameter.
    paintLineWidthHighlight: 7,
    style: styleTopographic,
    // Define the style url used by the topographic layer
    styleUrl: getStyleUrl(styleTopographic),
    // Activate/Deactivate the display of the grey background added by hideothers parameter.
    useHideOthersBg: false,

    // x coordinate to use if there is no x parameter defined in the url.
    // Must be define in each config.
    // x: 1288648.23,

    // y coordinate to use if there is no x parameter defined in the url.
    // Must be define in each config.
    // y: 6129702.78,

    // z coordinate to use if there is no x parameter defined in the url.
    // Must be define in each config.
    // z: 10,

    // Min zoom level of the map . Must be define in each config.
    // minZoom: 10,

    // Max zoom level of the map . Must be define in each config.
    // maxZoom: 14,

    // Max extent used by the full extent button. Must be define in each config.
    // extent: [1179506, 6055574, 1368764, 6200498],

    // Data extent that encapsulates all the stations. Must be define in each config.
    // dataExtent: [1179506, 6055574, 1368764, 6200498],

    // Function to define an approximate zoom level to zoom in when we switch the mode. Must be define in each config.
    // getZoomFromPreviousMode: (zoom) => {
    //   return zoom + 2;
    // },

    // Activate/Deactivate the display of line icons at the peripherie of the map.
    usePeripherieIcons: false,

    x: 0,

    y: 0,

    z: 0,

    /**
     * Auto mode specific property. When auto=true in the url.
     */
    // Min zoom level when the auto mode zoom in. Must be define in each config that use the auto mode.
    // minZoomForAutoMode: 12,

    // Max zoom level when the auto mode zoom out. Must be define in each config that use the auto mode.
    // maxZoomForAutoMode: 15,
  },

  trainParam: false,

  url: `${realtimeApiUrl}/?key=${VITE_API_KEY}`,

  /**
   * Notifications functionnalities.
   */
  useNotifications: false,

  // Active pointer move and pointer click event for train and station selection,
  // and remove also all map interactions
  userInteractions: true,

  vehiclesList: true,

  xyzParam: true,
};

export default common;
